import { gql } from '@apollo/client';
export const GET_OEE_SUMMARY = gql `
  query GetOEESummary($deviceId: String!, $from: Date!, $to: Date!) {
    deviceDataBuckets(
      from: $from
      intervalMinutes: 15
      to: $to
      deviceId: $deviceId
    ) {
      timestamp
      availability
      performance
      quality
      oee
    }
  }
`;
export const GET_DEVICE_OEE_DATA = gql `
  query GetDeviceOEEData($deviceId: String!, $from: Date!, $to: Date!) {
    deviceDataMany(
      filter: { topic: $deviceId, received_at: { gte: $from, lte: $to } }
      sort: RECEIVED_AT_ASC
      limit: 1000
    ) {
      _id
      payload
      received_at
      topic
    }
  }
`;
export const GET_DEVICES_FOR_OEE = gql `
  query GetDevicesForOEE {
    deviceMany(filter: { active: true }) {
      _id
      deviceName
      deviceId
      deviceLabel
      active
      createdAt
      sensorsUI {
        label
        sensorName
        type
        deviceGroupId
        optimumValue
      }
      backgroundImage
    }
  }
`;
export const GET_DAILY_BENCHMARKS = gql `
  query GetDailyBenchmarks($deviceId: String!, $from: Date!, $to: Date!) {
    dailyBenchmarkMany(
      filter: { topic: $deviceId, timestamp: { gte: $from, lte: $to } }
      sort: TIMESTAMP_ASC
    ) {
      _id
      availability
      performance
      quality
      oee
      timestamp
      topic
    }
  }
`;
export const OEE_DATA_SUBSCRIPTION = gql `
  subscription OeeDataSubscription($device_id: String!) {
    deviceData(device_id: $device_id) {
      _id
      payload
      topic
      received_at
    }
  }
`;
export const GET_LATEST_OEE_SUMMARY = gql `
  query GetLatestOeeSummary($deviceId: String!, $timeRange: TimeRangeInput!) {
    oeeByDevice(deviceId: $deviceId, timeRange: $timeRange) {
      availability
      performance
      quality
      oee
      timestamp
    }
  }
`;
export const GET_ALL_DEVICES_OEE_SUMMARY = gql `
  query GetAllDevicesOeeSummary($timeRange: TimeRangeInput!) {
    oeeData(timeRange: $timeRange) {
      deviceId
      availability
      performance
      quality
      oee
      timestamp
    }
  }
`;
export const GET_OEE_DASHBOARD_DATA = gql `
  query GetOeeDashboardData {
    oeeDashboard {
      currentOEE
      overallAvailability
      overallPerformance
      overallQuality
      deviceCount
      alertCount
      topPerformers {
        deviceId
        avgOEE
        avgAvailability
        avgPerformance
        avgQuality
      }
      underperformers {
        deviceId
        avgOEE
        avgAvailability
        avgPerformance
        avgQuality
      }
    }
  }
`;
export const GET_OEE_TRENDS = gql `
  query GetOeeTrends(
    $topic: String!
    $intervalMinutes: Int!
    $from: Date!
    $to: Date!
  ) {
    deviceDataBuckets(
      from: $from
      intervalMinutes: $intervalMinutes
      to: $to
      topic: $topic
    ) {
      timestamp
      availability
      performance
      quality
      oee
    }
  }
`;
export const PUBLISH_OEE_UPDATE = gql `
  mutation PublishOEEUpdate($input: OEEUpdateInput!) {
    publishOEEUpdate(input: $input) {
      success
      message
    }
  }
`;
export const CREATE_OEE_ALERT = gql `
  mutation CreateOEEAlert($input: OEEAlertInput!) {
    createOEEAlert(input: $input) {
      success
      alertId
    }
  }
`;
export const OEE_UPDATED_SUBSCRIPTION = gql `
  subscription OeeUpdated($deviceId: String!) {
    oeeUpdated(deviceId: $deviceId) {
      _id
      deviceId
      availability
      performance
      quality
      oee
      timestamp
      windowStart
      windowEnd
    }
  }
`;
export const OEE_ALERT_SUBSCRIPTION = gql `
  subscription OeeAlertTriggered($deviceId: String, $threshold: Float) {
    oeeAlertTriggered(deviceId: $deviceId, threshold: $threshold) {
      id
      deviceId
      alertType
      severity
      message
      threshold
      value
      timestamp
      resolved
    }
  }
`;
export const OEE_DASHBOARD_SUBSCRIPTION = gql `
  subscription OeeDashboardUpdates {
    oeeDashboardUpdates {
      currentOEE
      overallAvailability
      overallPerformance
      overallQuality
      deviceCount
      alertCount
      topPerformers {
        deviceId
        avgOEE
        avgAvailability
        avgPerformance
        avgQuality
      }
      underperformers {
        deviceId
        avgOEE
        avgAvailability
        avgPerformance
        avgQuality
      }
    }
  }
`;
export const DISCOVER_DEVICES = gql `
  mutation DiscoverDevices {
    discoverDevices {
      _id
      deviceName
      deviceLabel
      active
      sensorsUI {
        label
        sensorName
        type
        deviceGroupId
        optimumValue
      }
      backgroundImage
    }
  }
`;
export const REGISTER_DEVICE = gql `
  mutation RegisterDevice($deviceId: String!) {
    registerDevice(deviceId: $deviceId) {
      _id
      deviceName
      deviceLabel
      active
      sensorsUI {
        label
        sensorName
        type
        deviceGroupId
        optimumValue
      }
      backgroundImage
    }
  }
`;
export const GET_OEE_SUMMARIES = gql `
  query GetOEESummaries(
    $deviceGroupId: String
    $startDate: String
    $endDate: String
    $limit: Int
  ) {
    oeeSummaries(
      deviceGroupId: $deviceGroupId
      startDate: $startDate
      endDate: $endDate
      limit: $limit
    ) {
      _id
      availability
      performance
      quality
      oee
      notRunning
      timestamp
      windowStart
      windowEnd
      deviceGroupId
      deviceGroupName
      deviceLabel
      availabilityDataUsed {
        operatingTime
        plannedProductionTime
        calculation
        dataSources {
          mqttTopics
          statusFields
          productionWindow {
            start
            end
            timezone
          }
          deviceRecordsCount
        }
      }
      performanceDataUsed {
        totalCount
        idealCycleTime
        operatingTime
        calculation
        dataSources {
          productId
          productName
          idealCycleSource {
            type
            description
            daysUsed
            averageProductionRate
          }
          qualitySource {
            collection
            documentId
            isGenerated
          }
        }
      }
      qualityDataUsed {
        goodCount
        totalCount
        rejectCount
        calculation
        dataSources {
          baseData {
            date
            totalCount
            rejectCount
          }
          documentId
          generationMethod
          isGenerated
          productId
          productName
          sourceCollection
        }
      }
      downEventsSummary {
        totalEvents
        totalDowntimeSeconds
        events {
          startTime
          endTime
          downTime
          upTime
          duration
          reason
          sensorCount
          sensorValues
          isResolved
        }
      }
      products {
        productId
        productName
        goodCount
        rejectCount
        totalCount
        quality
        performance
        availability
        oee
        idealCycleTime
        contribution
      }
    }
  }
`;
// GraphQL query for product-level OEE quality statistics and trends for production lines
export const GET_PRODUCT_OEE_STATS = gql `
  query GetProductOEEStats(
    $deviceGroupId: String
    $startDate: String
    $endDate: String
  ) {
    productOEEStats(
      deviceGroupId: $deviceGroupId
      startDate: $startDate
      endDate: $endDate
    ) {
      products {
        productId
        productName
        totalGoodCount
        totalRejectCount
        totalCount
        averageQualityRate
        trendDirection
        dailyStats {
          date
          goodCount
          rejectCount
          totalCount
          qualityRate
        }
      }
      summary {
        totalProducts
        totalGoodCount
        totalRejectCount
        totalCount
        averageQualityRate
        trendDirection
        dailyStats {
          date
          goodCount
          rejectCount
          totalCount
          qualityRate
        }
      }
      timeRange {
        startDate
        endDate
        days
      }
    }
  }
`;
export const GET_OEE_SUMMARY_WITH_DETAILS = gql `
  query GetOEESummaryWithDetails(
    $deviceId: String!
    $startDate: String
    $endDate: String
  ) {
    oeeSummaries(
      deviceId: $deviceId
      startDate: $startDate
      endDate: $endDate
    ) {
      _id
      availability
      performance
      quality
      oee
      notRunning
      timestamp
      windowStart
      windowEnd
      deviceGroupId
      deviceGroupName
      deviceLabel
      calculatedAt
      totalCount
      goodCount
      rejectCount
      idealCycleTime
      availabilityDataUsed {
        operatingTime
        plannedProductionTime
        calculation
        dataSources {
          mqttTopics
          statusFields
          productionWindow {
            start
            end
            timezone
          }
          deviceRecordsCount
        }
      }
      performanceDataUsed {
        totalCount
        idealCycleTime
        operatingTime
        calculation
        dataSources {
          productId
          productName
          idealCycleSource {
            type
            description
            daysUsed
            averageProductionRate
          }
          qualitySource {
            collection
            documentId
            isGenerated
          }
        }
      }
      qualityDataUsed {
        goodCount
        totalCount
        rejectCount
        calculation
        dataSources {
          collection
          documentId
          isGenerated
        }
      }
      aggregatedQualityData {
        totalProductionCount
        totalRejectCount
        totalGoodCount
        overallQualityRate
        defectBreakdown {
          productId
          productName
          rejectCount
          defectRate
        }
      }
      downEventsSummary {
        totalEvents
        totalDowntimeSeconds
        events {
          startTime
          endTime
          downTime
          upTime
          duration
          reason
          sensorCount
          sensorValues
          isResolved
        }
      }
      productResults {
        productId
        productName
        totalCount
        goodCount
        rejectCount
        quality
        performance
        availability
        oee
        idealCycleTime
        contribution
      }
    }
  }
`;
export const GET_OEE_ALERTS = gql `
  query GetOeeAlerts(
    $deviceId: String
    $severity: AlertSeverity
    $resolved: Boolean
    $limit: Int
  ) {
    oeeAlerts(
      deviceId: $deviceId
      severity: $severity
      resolved: $resolved
      limit: $limit
    ) {
      _id
      deviceId
      deviceGroupId
      deviceGroupName
      alertType
      severity
      message
      threshold
      value
      timestamp
      resolved
      acknowledged
      acknowledgedBy
      acknowledgedAt
      resolvedBy
      resolvedAt
      escalationLevel
      assignedTo
      metadata
      notifications {
        inApp {
          sent
          sentAt
        }
        email {
          sent
          sentAt
        }
        sms {
          sent
          sentAt
        }
        whatsapp {
          sent
          sentAt
        }
      }
    }
  }
`;
export const RESOLVE_OEE_ALERT = gql `
  mutation ResolveOeeAlert($alertId: String!, $notes: String) {
    resolveOeeAlert(alertId: $alertId, notes: $notes)
  }
`;
export const GENERATE_OEE_SUMMARY = gql `
  mutation GenerateOEESummary($input: OEESummaryGenerationInput!) {
    generateOEESummary(input: $input) {
      success
      message
      results {
        _id
        deviceId
        availability
        performance
        quality
        oee
        notRunning
        productId
        productName
        timestamp
        windowStart
        windowEnd
        availabilityDataUsed {
          operatingTime
          plannedProductionTime
          calculation
          dataSources {
            mqttTopics
            statusFields
            productionWindow {
              start
              end
              timezone
            }
            deviceRecordsCount
          }
        }
        performanceDataUsed {
          totalCount
          idealCycleTime
          operatingTime
          calculation
          dataSources {
            productId
            productName
            idealCycleSource {
              type
              description
              daysUsed
              averageProductionRate
            }
            qualitySource {
              collection
              documentId
              isGenerated
            }
          }
        }
        qualityDataUsed {
          goodCount
          totalCount
          rejectCount
          calculation
          dataSources {
            productId
            productName
            sourceCollection
            documentId
            isGenerated
            generationMethod
            baseData {
              totalCount
              rejectCount
              date
            }
          }
        }
      }
      generatedAt
      targetDate
      totalDates
      processedDates
    }
  }
`;
export const GET_SYSTEM_DOWN_EVENTS = gql `
  query GetSystemDownEvents($filter: SystemDownEventsFilter) {
    systemDownEvents(filter: $filter) {
      _id
      deviceId
      alertId
      startTime
      endTime
      duration
      severity
      state
      reason
      status
      acknowledged
      acknowledgedBy
      acknowledgedAt
      resolved
      resolvedBy
      resolvedAt
      requiresReason
    }
  }
`;
export const CREATE_DOWNTIME_FROM_ALERT = gql `
  mutation CreateDowntimeFromAlert(
    $alertId: String!
    $reason: String!
    $endTime: Date
  ) {
    createDowntimeFromAlert(
      alertId: $alertId
      reason: $reason
      endTime: $endTime
    ) {
      success
      message
      downtimeEvent {
        _id
        startTime
        endTime
        state
        reason
      }
    }
  }
`;
export const UPDATE_DOWNTIME_REASON = gql `
  mutation UpdateDowntimeReason($alertId: String!, $reason: String!) {
    updateDowntimeReason(alertId: $alertId, reason: $reason)
  }
`;
export const GET_SENSOR_TRENDS = gql `
  query GetSensorTrends(
    $deviceId: String!
    $sensorName: String!
    $targetDate: String!
    $comparisonDays: Int = 10
  ) {
    sensorTrends(
      deviceId: $deviceId
      sensorName: $sensorName
      targetDate: $targetDate
      comparisonDays: $comparisonDays
    ) {
      targetDateData {
        timestamp
        value
        deviation
      }
      comparisonData {
        date
        averageValue
        averageDeviation
        dataPoints
      }
      sensorMetadata {
        sensorName
        sensorType
        unit
        optimumValue
      }
    }
  }
`;
export const GET_PRODUCT_TRENDS = gql `
  query GetProductTrends(
    $deviceId: String!
    $productId: String
    $targetDate: String!
    $comparisonDays: Int = 10
  ) {
    productTrends(
      deviceId: $deviceId
      productId: $productId
      targetDate: $targetDate
      comparisonDays: $comparisonDays
    ) {
      targetDateData {
        timestamp
        oee
        availability
        performance
        quality
        productId
        productName
      }
      comparisonData {
        date
        averageOEE
        averageAvailability
        averagePerformance
        averageQuality
        dataPoints
        productId
        productName
      }
      productMetadata {
        productId
        productName
        idealCycleTime
      }
    }
  }
`;
export const GET_SENSOR_DEVIATION_ANALYSIS = gql `
  query GetSensorDeviationAnalysis(
    $deviceId: String!
    $targetDate: String!
    $days: Int = 10
  ) {
    sensorDeviationAnalysis(
      deviceId: $deviceId
      targetDate: $targetDate
      days: $days
    ) {
      sensors {
        sensorName
        currentValue
        currentDeviation
        averageDeviation
        deviationTrend
        status
      }
      summary {
        totalSensors
        criticalDeviations
        warnings
        optimal
        timestamp
      }
    }
  }
`;
export const GET_DEVICE_GROUPS = gql `
  query GetDeviceGroups {
    deviceGroupMany(filter: { active: true }) {
      _id
      label
      description
      active
      sensorUI
      viewType
      ordering
      products {
        productId
        productName
        isDefault
      }
    }
  }
`;
export const GET_OEE_TRENDS_30_DAYS = gql `
  query GetOeeTrends30Days($startDate: String!, $endDate: String!) {
    oeeSummaries(startDate: $startDate, endDate: $endDate, limit: 5000) {
      _id
      deviceGroupId
      deviceGroupName
      deviceLabel
      availability
      performance
      quality
      oee
      notRunning
      timestamp
    }
  }
`;
export const GET_OEE_QUALITY_SUMMARY = gql `
  query GetOEEQualitySummary($deviceGroupId: String!, $reportDate: String!) {
    getOEEQualitySummary(deviceGroupId: $deviceGroupId, reportDate: $reportDate) {
      reportDate
      deviceGroupId
      nonRejectTotal
      rejectTotal
      totalRecords
      createdAt
      updatedAt
    }
  }
`;
export const GET_OEE_QUALITY_SUMMARY_BY_PRODUCTS = gql `
  query GetOEEQualitySummaryByProducts($deviceGroupId: String!, $reportDate: String!) {
    getOEEQualitySummaryByProducts(deviceGroupId: $deviceGroupId, reportDate: $reportDate) {
      deviceGroupId
      deviceGroupLabel
      reportDate
      nonRejectTotal
      rejectTotal
      totalRecords
      totalCount
      sourceDeviceGroupId
      matchedWithCSV
      totalProducts
      productIds
      createdAt
      updatedAt
      error
    }
  }
`;
export const OEE_SENSOR_READINGS = gql `
  query OeeSensorReadings(
    $deviceGroupId: String!
    $startDate: String!
    $endDate: String!
  ) {
    oeeSensorReadings(
      deviceGroupId: $deviceGroupId
      startDate: $startDate
      endDate: $endDate
    ) {
      receivedAt
      sensorName
      sensorType
      deviceName
      rawValue
      actualValue
      minConvertedValue
      maxConvertedValue
      role
    }
  }
`;
export const GET_OEE_QUALITY_SUMMARY_BY_DEVICE_GROUP = gql `
  query GetOEEQualitySummaryByDeviceGroup(
    $deviceGroupId: String
    $startDate: String
    $endDate: String
    $limit: Int
  ) {
    oeeQualitySummaryByDeviceGroup(
      deviceGroupId: $deviceGroupId
      startDate: $startDate
      endDate: $endDate
      limit: $limit
    ) {
      _id
      deviceGroupId
      originalDeviceGroupId
      reportDate
      nonRejectTotal
      rejectTotal
      totalRecords
      totalCount
      qualityRate
      createdAt
      updatedAt
      isFallbackData
    }
  }
`;
